// ─── FiberOps DESIGN TOKENS ───
// Jeden plik, wszystkie ekrany korzystają z tego samego słownika.
// Zmiana koloru tutaj → zmiana w całej aplikacji.

export const TOKENS = {
  colors: {
    // v10 faza „Kierunek A": POWIERZCHNIE przez CSS variables (--fo-*), ustawiane
    // przez useTheme na :root (jasny/ciemny bez rebuilda; fallback = dark).
    // Kolory STANÓW kaskady są wspólne dla obu motywów — zostają literalne.
    bgMain: "var(--fo-bg, #101012)",         // Deep Black — tło główne
    bgCard: "var(--fo-bg-card, #18181b)",    // Zinc 950 — tło kart
    bgCardSurface: "var(--fo-bg-card-surface, rgba(39, 39, 42, 0.7))", // Glassmorphism surface
    bgCardHover: "var(--fo-bg-card-hover, #3f3f46)", // Zinc 700 — hover state
    tileBg: "var(--fo-tile-bg, #2a2a2e)",    // Tło kafelków wyboru

    fiberOrange: "#ea580c",    // Orange 600 — status "w toku" (kaskada) — NIE brand!
    // v10 (2026-07-05, kierunek A Evolution): BRAND ODDZIELONY od status-pomaranczu.
    // Chrome aplikacji (logo UBM, pigulki, CTA, aktywna zakladka) = brand.
    brand: "#f97316",          // Orange 500 — brand FiberOps/UBM
    fiberBright: "#fdba74",    // Orange 300 — highlight
    schematicOrange: "#e8853a",// Pomarańczowy w schematach SVG

    orangeGlow: "rgba(234, 88, 12, 0.3)",
    orangeGlowStrong: "rgba(234, 88, 12, 0.5)",

    textPrimary: "var(--fo-txt, #ffffff)",
    textSecondary: "var(--fo-txt2, #a1a1aa)",  // Zinc 400
    textMuted: "var(--fo-mut, #71717a)",       // Zinc 500
    textDisabled: "var(--fo-dis, #52525b)",    // Zinc 600

    border: "var(--fo-brd, rgba(255, 255, 255, 0.05))",
    borderDashed: "var(--fo-brd-dashed, rgba(255, 255, 255, 0.10))",
    borderSelected: "#e8853a",

    // Kolory tras kabli w schematach (T3 Wohnungssteiger)
    cableGreen: "#5dca88",
    cableBlue: "#85b7eb",
    schematicWall: "#e0ddd6",
    schematicLabel: "#9a9890",
  },

  radius: {
    sm: 6,
    md: 8,
    lg: 12,
    xl: 16,
    tile: 14,    // Kafelki wyboru
    pill: 20,    // Toggle PL/DE
  },

  font: {
    sans: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    // CZYSTE ZERO (Martin 13.08): Chivo Mono, self-hosted w /fonts (index.html).
    // Pomiar glifów wykazał: JetBrains/Roboto/Source Code/IBM Plex/Fira/Consolas
    // i praktycznie każdy inny mono ZNACZY zero (kropka/ukośnik) — Chivo Mono ma
    // czyste. Font jest w precache SW, więc fallback na systemowy monospace
    // (znaczone 0) zdarzy się tylko przy pierwszym uruchomieniu bez sieci.
    mono: "'Chivo Mono', monospace",
  },

  // Safe area aware padding
  safeArea: {
    headerPadding: "calc(32px + env(safe-area-inset-top, 0px)) 24px 0",
    footerPadding: "24px 24px calc(24px + env(safe-area-inset-bottom, 0px))",
    contentPadding: "24px 24px 160px",
  },

  layout: {
    maxWidth: 430,
    minHeight: "100dvh",
  },
};

export default TOKENS;

// ─── Aliasy (flat API) — używane przez Shell/OfflineBanner/SlotButton/nowe ekrany ───
// Zachowane dla kompatybilności z komponentami napisanymi po 85a3c1a.
export const COLORS = {
  bg: TOKENS.colors.bgMain,
  bgCard: TOKENS.colors.bgCard,
  bgHover: TOKENS.colors.bgCardHover,
  accent: TOKENS.colors.fiberOrange,
  accentHover: TOKENS.colors.fiberBright,
  accentGlow: TOKENS.colors.orangeGlow,
  text: TOKENS.colors.textPrimary,
  textMuted: TOKENS.colors.textSecondary,
  textDim: TOKENS.colors.textMuted,
  success: "#22c55e",
  warning: "#eab308",
  error: "var(--fo-red, #ef4444)",
  border: "var(--fo-brd-strong, #27272a)",
  borderSoft: TOKENS.colors.border,
};

export const FONTS = {
  body: TOKENS.font.sans,
  mono: TOKENS.font.mono,
};

export const RADIUS = { ...TOKENS.radius, pill: 999 };

export const SAFE = {
  top: "calc(16px + env(safe-area-inset-top, 0px))",
  bottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
};

// Wspólne style helpers
export const card = {
  background: COLORS.bgCard,
  border: `1px solid ${COLORS.border}`,
  borderRadius: RADIUS.lg,
  padding: 16,
};

export const button = {
  primary: {
    background: COLORS.accent,
    color: "#fff",
    border: "none",
    borderRadius: RADIUS.md,
    padding: "12px 20px",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: `0 4px 12px ${COLORS.accentGlow}`,
  },
  secondary: {
    background: "transparent",
    color: COLORS.text,
    border: `1px solid ${COLORS.border}`,
    borderRadius: RADIUS.md,
    padding: "10px 18px",
    fontSize: 14,
    cursor: "pointer",
  },
};

// ─── Warianty „tuszu" dla obwódek (v10: theme-aware) ────────────────────────
// Kontrast do tła: na ciemnym = biel z alfa, na jasnym = czerń z alfa (--fo-ink-*).
// Nazwy WHITE_* zachowane dla kompatybilności importów.
export const WHITE_90 = "var(--fo-ink-90, rgba(255, 255, 255, 0.9))";
export const WHITE_45 = "var(--fo-ink-45, rgba(255, 255, 255, 0.45))"; // K9: empty-slot border (bielsza niż 0.25)
export const WHITE_25 = "var(--fo-ink-25, rgba(255, 255, 255, 0.25))";
export const WHITE_15 = "var(--fo-ink-15, rgba(255, 255, 255, 0.15))";
export const WHITE_10 = "var(--fo-ink-10, rgba(255, 255, 255, 0.1))";

// ─── ANEKS-2 (2026-08-13): kolory 4 liczników statusów WE ───────────────────
// JEDNO źródło dla kafelków „Podsumowanie" (AdminTaskListScreen, zakres Team+KW)
// i paska liczników na widoku adresu (WeCountersBar, zakres adres). Martin:
// „kolory niosą znaczenie" — te same 4 kolory w obu miejscach, zawsze w tej kolejności.
export const WE_COUNTER_COLORS = {
  wePlanned:    TOKENS.colors.textPrimary,  // TA razem — czarny/biały wg motywu
  taMounted:    "#3b82f6",                  // TA zamontowane — niebieski (jak ai_verified)
  vorbereitung: "#7c3aed",                  // Vorbereitung — fioletowy (jak kunde_nicht_da)
  wInProgress:  TOKENS.colors.fiberOrange,  // W toku — pomarańcz statusowy #ea580c
};

// ─── 7 STANÓW SLOTÓW (ADR-004 + K22b-1) ─────────────────────────────────────
// Używane w FloorTile, KellerView, FloorWEList, progress bars.
// Priorytet (najgorszy wygrywa):
//   ai_error > in_progress > ready_for_check > processing > ai_verified > verified > empty
//
// K22b-1 (ADR-B11): ai_verified (niebieski) — wszystkie zdjęcia slotu mają
// ai_verdict='ok' z Gemini. Wyżej niż verified (zielony Etap A placeholder).
// Różnica wizualna od processing: brak pulse (processing = pulsująca animacja).
export const STATUS_COLORS = {
  empty: {
    // K9 (Martin): bielsza ramka — 0.25 było za słabo widoczne.
    // v10: przez --fo-ink-45 (na jasnym motywie „tusz" = czerń z alfa).
    border: "var(--fo-ink-45, rgba(255, 255, 255, 0.45))",
    bg: "var(--fo-bg-card-surface, rgba(39,39,42,0.7))",
    accent: "var(--fo-mut, #71717a)",
    label: "Puste",
    // K7: opacity 1 — ramka decyduje o stanie, nie wyblakłość
    opacity: 1,
  },
  in_progress: {
    border: "#ea580c",
    // v10 Martin 06.07: alfa tła per motyw — na jasnym subtelniej (0.03), żeby
    // pomarańczowe kafelki nie konkurowały z czerwonym błędem.
    bg: "rgba(234,88,12, var(--fo-orange-bg-a, 0.06))",
    accent: "#ea580c",
    label: "W toku",
    opacity: 1,
  },
  ready_for_check: {
    border: "#eab308",
    bg: "rgba(234,179,8,0.08)",
    accent: "#eab308",
    label: "Do sprawdzenia",
    opacity: 1,
  },
  processing: {
    border: "#3b82f6",
    bg: "rgba(59,130,246,0.08)",
    accent: "#3b82f6",
    label: "Analizuje AI",
    opacity: 1,
    pulse: true,
  },
  verified: {
    border: "#22c55e",
    bg: "rgba(34,197,94,0.08)",
    accent: "#22c55e",
    label: "Sprawdzone",
    opacity: 1,
  },
  ai_error: {
    border: "var(--fo-red, #ef4444)",
    // v10 Martin 06.07: na jasnym tło błędu MOCNIEJSZE (0.18) — kafelek błędu ma
    // się odcinać od pomarańczowych od pierwszego rzutu oka.
    bg: "rgba(var(--fo-red-rgb, 239,68,68), var(--fo-red-bg-a, 0.08))",
    accent: "var(--fo-red, #ef4444)",
    label: "Błąd AI",
    opacity: 1,
  },
  // K22b-1 (ADR-B11): ai_verified — niebieski, wszystkie zdjęcia ok w Gemini.
  // Priorytet wyżej niż 'verified' (zielony Etap A placeholder).
  // Wizualnie różni się od 'processing' brakiem pulse (processing pulsuje).
  ai_verified: {
    border: "#3b82f6",
    bg: "rgba(59,130,246,0.1)",
    accent: "#3b82f6",
    label: "Zweryfikowane AI",
    opacity: 1,
  },
  // B3.5.6 KND-MVP (2026-04-28): kunde_nicht_da — fioletowy, WE oznaczone "klient
  // nieobecny" + 3 wymagane sloty completed+ai_ok (ta_name, ta_zu_qr, laser_test).
  // Reguła: fioletowy istnieje TYLKO na poziomie WE — agregat wyższy (piętro/adres)
  // traktuje fioletowy WE jako verified-equivalent (niebieski w propagacji).
  // STATUS_PRIORITY = 3 (TĄ SAMĄ wagę co ai_verified), ale w worstStatus
  // tie-breaker nie znaczy bo to dwa różne "OK" stany — agregator widzi oba
  // jak verified.
  kunde_nicht_da: {
    border: "#7c3aed",          // Violet 600
    bg: "rgba(124,58,237,0.1)",
    accent: "#a78bfa",           // Violet 400 (highlight)
    label: "Kunde nicht da",
    opacity: 1,
  },
  // Gdy technik wyłączył "KG istnieje" — cały kafelek zgaszony
  kg_disabled: {
    border: "var(--fo-brd, rgba(255,255,255,0.05))",
    bg: "var(--fo-sub, rgba(39,39,42,0.3))",
    accent: "var(--fo-dis, #52525b)",
    label: "Brak piwnicy",
    opacity: 0.35,
  },
};

/**
 * Priorytet statusów (najgorszy wygrywa).
 *
 * B3.6.3 (2026-05-03 sobota wieczór, decyzja Martin): NOWA hierarchia.
 * Verified WYGRYWA nad ai_verified — sygnał "niektóre sloty wciąż czekają na AI"
 * w mix verified+ai_verified. Wszystkie ai_verified → niebieski (sync OK).
 *
 * Kaskada:
 *   ai_error (7)        czerwony — wygrywa zawsze, migruje do adresu
 *   in_progress (6)     pomarańczowy — niewystarczająca ilość photos
 *   verified (5)        zielony — czeka na AI lub all-err retry (NOWE: wyżej niż ai_verified)
 *   ready_for_check (5) zielony — synonim verified (technik confirm, czeka)
 *   processing (3)      niebieski w trakcie — przejściowy
 *   ai_verified (2)     niebieski — accepted (≥1 ok/acpt), najlepszy "OK"
 *   kunde_nicht_da (2)  fioletowy WE — ta sama waga co ai_verified (B3.5.6, agregat traktuje jak OK)
 *   empty (1)           biały — najniższy
 */
export const STATUS_PRIORITY = {
  ai_error: 7,
  in_progress: 6,
  ready_for_check: 5,
  verified: 5,           // B3.6.3: TA SAMA waga co ready_for_check (oba "czekają na AI")
  processing: 3,
  ai_verified: 2,        // B3.6.3: niżej niż verified (zielony wygrywa nad niebieskim w mix)
  kunde_nicht_da: 2,     // B3.5.6: ta sama waga co ai_verified
  empty: 1,
};

/** Wybiera najgorszy status z listy (ADR-004). */
export function worstStatus(statuses) {
  if (!statuses?.length) return "empty";
  let worst = statuses[0];
  let worstP = STATUS_PRIORITY[worst] ?? 0;
  for (let i = 1; i < statuses.length; i++) {
    const p = STATUS_PRIORITY[statuses[i]] ?? 0;
    if (p > worstP) { worst = statuses[i]; worstP = p; }
  }
  return worst;
}

// ─── B3.5 — getTileColor (sprint A2/A6) ────────────────────────────────────
//
// Jedno źródło prawdy dla logiki kolorów kafelków (slot/piętro/adres).
// Mapuje 7-stanowy system ADR-004 na 4-stanową kaskadę kolorów Martina:
//
//   🟠 Pomarańczowy (in_progress)   — incomplete photos, najwyższy priorytet (action)
//   🔴 Czerwony (ai_error)           — AI fail/err
//   🟢 Zielony (verified/ready_for_check) — komplet zdjęć, AI czeka/oceniło OK Etapem A
//   🔵 Niebieski (ai_verified)        — wszystkie zdjęcia AI verified (auto/override)
//   ⚪ Biały subtelny (empty)         — pojedynczy pusty slot (na agregacjach mix → orange)
//   🟦 Niebieski pulse (processing)   — AI w toku
//
// Argument `state`: jeden z 7 stanów lub 'kg_disabled'.
// Opcje:
//   - width: szerokość bordera w px (default 2)
//   - fallback: state używany gdy `state` nieznany (default 'empty')
export function getTileColor(state, options = {}) {
  const { width = 2, fallback = "empty" } = options;
  // Mapowanie sprint A2/A6: ready_for_check → ZIELONY (sprint: "AI czeka").
  // STATUS_COLORS aktualnie ma żółty dla ready_for_check (informacyjny). W kaskadzie
  // 4-stanowej traktujemy go jak verified (komplet zdjęć, AI waiting/ok).
  const SIMPLIFIED = {
    empty: STATUS_COLORS.empty,
    in_progress: STATUS_COLORS.in_progress,
    ai_error: STATUS_COLORS.ai_error,
    ready_for_check: STATUS_COLORS.verified,
    processing: STATUS_COLORS.processing,
    verified: STATUS_COLORS.verified,
    ai_verified: STATUS_COLORS.ai_verified,
    kunde_nicht_da: STATUS_COLORS.kunde_nicht_da,  // B3.5.6 KND-MVP — fioletowy
    kg_disabled: STATUS_COLORS.kg_disabled,
  };
  const c = SIMPLIFIED[state] || SIMPLIFIED[fallback] || STATUS_COLORS.empty;
  return {
    border: `${width}px solid ${c.border}`,
    borderColor: c.border,
    bg: c.bg,
    accent: c.accent,
    label: c.label,
    pulse: c.pulse || false,
    opacity: c.opacity,
  };
}
